'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { Check, X } from 'phosphor-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { c } from 'ui-components';
import { FacetAccordion } from '../../FacetAccordion';
import { deselectOwnershipFilter } from '../../utils/clearOwnershipFilter';
import { getPageSizeOptions } from '../../utils/pageSize';
import { CategoryFacet } from '../CategoryFacet';
import { OwnershipFacet } from '../OwnershipFacet';
import { PriceFacet } from '../PriceFacet';
import { PublisherFacet } from '../PublisherFacet';
import { RatingFacet } from '../RatingFacet';
import { ReleaseDateFacet } from '../ReleaseDateFacet';
import { SaleFacet } from '../SaleFacet';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { useDialogFocusTrap } from '../hooks/useDialogFocusTrap';
import { useSearchV2ResultsPerPage } from '../hooks/useSearchV2ResultsPerPage';
import { useSearchV2Sort } from '../hooks/useSearchV2Sort';
import { MobileFilterDrawerProps } from './MobileFilterDrawer.types';

const countSelected = (values: { state: string }[] | undefined) =>
  values?.filter((v) => v.state === 'selected').length ?? 0;

type DrawerOption = { value: string; label: string };

/**
 * Single-choice option rows for the drawer's non-facet sections. Selection is
 * carried on `aria-pressed` (the repo's convention for a styled single-select
 * button list) rather than a radiogroup, whose arrow-key contract these rows
 * don't implement.
 */
const DrawerOptionList = ({
  options,
  selectedValue,
  onSelect,
  disabled,
  ariaLabel,
}: {
  options: DrawerOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  disabled: boolean;
  ariaLabel: string;
}) => (
  <div className="flex flex-col" role="group" aria-label={ariaLabel}>
    {options.map((option) => {
      const isSelected = option.value === selectedValue;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onSelect(option.value)}
          disabled={disabled}
          aria-pressed={isSelected}
          className={c(
            'flex items-center justify-between rounded-lg px-1 py-3 text-sm disabled:cursor-not-allowed disabled:text-grey-600',
            isSelected
              ? 'font-medium text-white'
              : 'text-grey-300 hover:text-white'
          )}
        >
          {option.label}
          {isSelected && <Check size={16} className="shrink-0 text-white" />}
        </button>
      );
    })}
  </div>
);

/**
 * Full-screen mobile filter drawer for the SSR search path: every facet in its
 * `mobile` variant plus Sort and Results-per-page, a live result count, and a
 * clear-all that also resets the (non-Coveo) ownership filter.
 *
 * All search state flows through the generated controller hooks, so a filter
 * applied here hits the same engine, URL serialization and analytics as the
 * desktop sidebar/horizontal bar — the drawer owns no search state of its own,
 * only which sections are expanded.
 */
export const MobileFilterDrawer = ({
  isOpen,
  onClose,
  className = '',
}: MobileFilterDrawerProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();
  const session = useSession();
  const searchParams = useSearchParams();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((section: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const {
    currentSortValue,
    sortCriteria,
    handleSort,
    isReady: isSortReady,
  } = useSearchV2Sort();

  // Facet controllers, subscribed here for the selection counts; each facet
  // component subscribes independently for its own values.
  const controllers = useSearchControllers();
  const priceFacet = controllers.usePriceFacet();
  const ratingFacet = controllers.useRatingFacet();
  const saleFacet = controllers.useSaleFacet();
  const publisherFacet = controllers.usePublisherFacet();
  const dateFacet = controllers.useReleaseDateFacet();
  const categoryFacet = controllers.useCategoryFacet();
  const breadcrumbManager = controllers.useBreadcrumbManager();

  const isAuthenticated = session.status === 'authenticated';
  // Per ADR 001 the ownership filter is a query-string param, not a Coveo
  // facet — and it is signed-in only, so an anonymous visitor's deep-linked
  // `hideOwnership` neither renders a section nor counts as a selection.
  const ownershipChecked =
    isAuthenticated && searchParams?.get('hideOwnership') === 'true';

  const totalSelectedCount = useMemo(
    () =>
      countSelected(priceFacet.state.values) +
      countSelected(ratingFacet.state.values) +
      countSelected(saleFacet.state.values) +
      countSelected(publisherFacet.state.values) +
      countSelected(dateFacet.state.values) +
      countSelected(categoryFacet.state.values) +
      (ownershipChecked ? 1 : 0),
    [
      priceFacet.state.values,
      ratingFacet.state.values,
      saleFacet.state.values,
      publisherFacet.state.values,
      dateFacet.state.values,
      categoryFacet.state.values,
      ownershipChecked,
    ]
  );

  const {
    state: resultsPerPageState,
    methods: resultsPerPageMethods,
    selectResultsPerPage,
  } = useSearchV2ResultsPerPage();

  const { state: querySummaryState } = controllers.useQuerySummary();

  // `methods` only lands once hydrateStaticState resolves — permanently absent
  // if it fails — so a Coveo-backed clear renders disabled instead of
  // swallowing the click, the same readiness contract as the facet components
  // themselves. Ownership is URL-backed and clears without the engine, so an
  // ownership-only selection stays clearable through hydration.
  const coveoSelectedCount = totalSelectedCount - (ownershipChecked ? 1 : 0);
  const canClearFacets =
    Boolean(breadcrumbManager.methods) ||
    (ownershipChecked && coveoSelectedCount === 0);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useDialogFocusTrap({ isOpen, containerRef: dialogRef, onClose });

  // One deselectAll across every active facet value, as the breadcrumb bar's
  // Clear all does: deselecting facet by facet dispatches one search each and
  // aborts all but the last, which surfaces as rejected-search console errors.
  // ec_category is in `facetBreadcrumbs` too — only the chips row filters it
  // out of display — so the category facet clears with the rest.
  const handleClearAll = useCallback(() => {
    breadcrumbManager.methods?.deselectAll();
    if (ownershipChecked) {
      deselectOwnershipFilter();
    }
  }, [breadcrumbManager.methods, ownershipChecked]);

  const sortOptions = useMemo(
    () =>
      sortCriteria.map((option) => ({
        value: option.value,
        label: getMicrocopyValue(
          searchLabelsMicrocopy,
          `search.${option.value}`,
          option.value.charAt(0).toUpperCase() + option.value.slice(1)
        ),
      })),
    [sortCriteria, searchLabelsMicrocopy]
  );

  const resultsPerPageOptions = useMemo(
    () =>
      getPageSizeOptions(resultsPerPageState.numberOfResults).map((size) => ({
        value: String(size),
        label: String(size),
      })),
    [resultsPerPageState.numberOfResults]
  );

  if (!isOpen) return null;

  const sortByLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.sort-by',
    'Sort by'
  );
  const resultsPerPageLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.results-per-page',
    'Results'
  );
  const resultsLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.results-count-label',
    'Results'
  );
  const resultTotal = querySummaryState.total.toLocaleString();

  return (
    <div
      ref={dialogRef}
      className={c('fixed inset-0 z-50 flex flex-col bg-black', className)}
      role="dialog"
      aria-modal="true"
      aria-label={getMicrocopyValue(
        searchLabelsMicrocopy,
        'search.sort-and-filter',
        'Sort and Filter'
      )}
    >
      <div className="flex h-14 shrink-0 items-center justify-end px-4">
        <button
          type="button"
          onClick={onClose}
          aria-label={getMicrocopyValue(
            searchLabelsMicrocopy,
            'search.close',
            'Close'
          )}
          className="-mr-2.5 flex h-11 w-11 items-center justify-center text-grey-400 hover:text-white"
        >
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <CategoryFacet
          variant="mobile"
          isOpen={openSections.has('categories')}
          onToggle={() => toggleSection('categories')}
        />
        <PriceFacet
          variant="mobile"
          isOpen={openSections.has('price')}
          onToggle={() => toggleSection('price')}
        />
        <SaleFacet
          variant="mobile"
          isOpen={openSections.has('sale')}
          onToggle={() => toggleSection('sale')}
        />
        <RatingFacet
          variant="mobile"
          isOpen={openSections.has('rating')}
          onToggle={() => toggleSection('rating')}
        />
        <PublisherFacet
          variant="mobile"
          isOpen={openSections.has('publisher')}
          onToggle={() => toggleSection('publisher')}
        />
        <ReleaseDateFacet
          variant="mobile"
          isOpen={openSections.has('date')}
          onToggle={() => toggleSection('date')}
        />
        <OwnershipFacet
          variant="mobile"
          isOpen={openSections.has('ownership')}
          onToggle={() => toggleSection('ownership')}
        />

        <FacetAccordion
          title={sortByLabel}
          variant="mobile"
          isOpen={openSections.has('sort')}
          onToggle={() => toggleSection('sort')}
        >
          <DrawerOptionList
            options={sortOptions}
            selectedValue={currentSortValue}
            onSelect={handleSort}
            disabled={!isSortReady}
            ariaLabel={sortByLabel}
          />
        </FacetAccordion>

        <FacetAccordion
          title={resultsPerPageLabel}
          variant="mobile"
          isOpen={openSections.has('results')}
          onToggle={() => toggleSection('results')}
        >
          <DrawerOptionList
            options={resultsPerPageOptions}
            selectedValue={String(resultsPerPageState.numberOfResults)}
            onSelect={(value) => selectResultsPerPage(parseInt(value, 10))}
            disabled={!resultsPerPageMethods}
            ariaLabel={resultsPerPageLabel}
          />
        </FacetAccordion>
      </div>

      <div className="shrink-0 border-t border-grey-800 px-4 pb-6 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="flex w-full items-center justify-center rounded-full border-2 border-blue py-3 text-sm font-semibold uppercase tracking-wider text-blue hover:bg-blue/10"
        >
          {getMicrocopyValue(
            searchLabelsMicrocopy,
            'search.view-results',
            'View results'
          )}{' '}
          ({resultTotal})
        </button>
        <span role="status" aria-live="polite" className="sr-only">
          {`${resultsLabel} ${resultTotal}`}
        </span>
        <button
          type="button"
          onClick={handleClearAll}
          disabled={!canClearFacets}
          className="mt-3 flex min-h-11 w-full items-center justify-center text-sm text-grey-400 hover:text-grey-200 disabled:cursor-not-allowed disabled:text-grey-600"
        >
          {`${getMicrocopyValue(searchLabelsMicrocopy, 'search.clear', 'Clear')}${
            totalSelectedCount > 0 ? ` (${totalSelectedCount})` : ''
          }`}
        </button>
      </div>
    </div>
  );
};
